<script>
/**
 * Xero venues
 * @version 1.0.0
 * @since
 */

import xeroGuard from '@/views/platform/venue/xero/settings/xeroGuard';
import VSelectSearch from '@/components/v3/patterns/VSelectSearch/index';
import EzTable from '@/components/ui/Table/EzTable';
import EzLoadMore from '@/components/ui/LoadMore/EzLoadMore.vue';
import EzSpinner from '@/components/ui/Spinner/EzSpinner.vue';
import VVenueEntityInfo from '@/components/v3/patterns/VVenueEntityInfo/index';
import { venueGetVenues, venueGetExternalVenues, venueXeroAddVenue } from '@/api/endpoints/xero';
import flash from '@/components/ui/FlashMessage';
import EmptyState from '@/views/common/empty-state/EmptyState';
import EzLoader from '@/components/ui/Loader/EzLoader';
import { mapGetters } from 'vuex';
import { LOADING_KEY } from '@/util/constants';
import { debounce } from '@/util/utils';
import EzFilterList from '@/components/ui/FilterList';
import EzInput from '@/components/ui/Input/EzInput.vue';

export default {
  mixins: [xeroGuard],
  components: {
    VSelectSearch,
    EzTable,
    EzLoadMore,
    EzSpinner,
    VVenueEntityInfo,
    EmptyState,
    EzLoader,
    EzFilterList,
    EzInput,
  },
  props: {
    venueId: {
      type: Number,
    },
  },
  data() {
    return {
      isLoaderShown: false,
      isLoadingMore: false,
      contacts: [],
      venues: [],
      meta: {},
      columns: [],
      columnsHeaders: {},
      filters: {
        term: null,
      },
    };
  },
  computed: {
    ...mapGetters('loading', ['isSomeLoading']),
    isLoadingQB() {
      return this.isSomeLoading([LOADING_KEY.VENUE_UPDATE_XERO_SUPPLIERS]);
    },
    columnsNested() {
      return ['name', 'xeroContact'];
    },
    tableHeaders() {
      return {
        name: () => 'Outlet',
        xeroContact: () => 'Xero Contact',
      };
    },
    entity() {
      return this.$t('global.venues');
    },
    isLoading() {
      return this.isLoaderShown || this.isLoadingQB;
    },
  },
  methods: {
    vendorSelected(contact, venueId) {
      this.saveChanges({
        id: venueId,
        contactId: contact.xeroId ? contact.xeroId : null,
      });
    },
    async saveChanges(item) {
      try {
        this.isLoaderShown = true;
        await venueXeroAddVenue(this.venueId, {
          items: [
            {
              externalId: item.contactId,
              groupVenueId: item.id,
            },
          ],
        });
        const index = this.venues.findIndex(venue => venue.id === item.id);
        if (item.contactId) {
          this.venues[index].integrationEntity = {
            id: item.contactId,
            name: item.contactName,
          };
        } else {
          this.venues[index].integrationEntity = null;
        }
        this.isLoaderShown = false;
        flash.success({
          title: this.$t('quickBooks.settings.venues.flashMessages.success.title'),
        });
      } catch (e) {
        this.isLoaderShown = false;
        flash.error({
          title: this.$t('quickBooks.settings.venues.flashMessages.error.title'),
        });
      }
    },
    async getVenuesListAndContacts() {
      this.isLoaderShown = true;
      const { data: contacts } = await venueGetExternalVenues(this.venueId);
      this.contacts = contacts.data;

      const { data: venues } = await venueGetVenues({
        id: this.venueId,
        ...this.filters,
      });
      this.meta = venues.meta;
      this.venues = venues.data;
      this.isLoaderShown = false;
    },
    async fetchMoreSuppliers() {
      this.isLoadingMore = true;
      const { data: venues } = await venueGetVenues({
        id: this.venueId,
        ...this.meta,
        ...this.filters,
      });
      this.venues = [...this.venues, ...venues.data];
      this.meta = venues.meta;
      this.isLoadingMore = false;
    },
    async resetFilters() {
      Object.keys(this.filters).forEach(key => {
        this.filters[key] = null;
      });
      await this.getVenuesListAndContacts();
    },
    updateFilters: debounce(async function deb(filterName, event) {
      if (filterName === 'search') {
        if (event.length !== 0 && event.length < 3) return;
        this.filters = { ...this.filters, term: event.length ? event : null };
      }
      this.selectedDistributors = [];
      await this.getVenuesListAndContacts();
    }, 300),
  },
  created() {
    this.getVenuesListAndContacts();
  },
};
</script>
<template>
  <div>
    <ez-filter-list
      :filters="filters"
      @filterUpdated="updateFilters"
      @resetFilter="resetFilters"
      class="mb-24"
    >
      <ez-input
        formKey="filters"
        name="search"
        class="search"
        :placeholder="`Search for a ${$t('global.venue')}`"
      >
        <template #prefix>
          <font-awesome-icon icon="search" />
        </template>
      </ez-input>
    </ez-filter-list>
    <div>
      <ez-table
        v-if="venues.length"
        class="sync-table"
        :disable-hover="true"
        :data="venues"
        :columns="columnsNested"
        :headers="tableHeaders"
        :columnProps="{
          name: { class: 'supplier-cell' },
          xeroContact: { class: 'xxl-cell' },
        }"
      >
        <template #cell-name="{ row }">
          <div class="supplier-name">
            <v-venue-entity-info class="cursor-pointer" :venue="row" />
          </div>
        </template>
        <template #cell-xeroContact="{ row }">
          <v-select-search
            :data="contacts"
            :search="`/venues/${venueId}/xero/external-venues`"
            value-property="xeroId"
            placeholder="Select Xero Contact"
            :selected="row.integrationEntity?.id ?? null"
            @selected="obj => vendorSelected(obj, row.id)"
          />
        </template>
      </ez-table>
      <template v-if="!venues.length">
        <empty-state>
          <template #badge>
            <img src="@/assets/no-venue-empty-state.svg" width="160" alt="" />
          </template>
          <template #title>{{ $t('xero.settings.venues.emptyState.title') }}</template>
        </empty-state>
      </template>
      <div v-if="isLoadingMore" class="u-text-center mt-12">
        <ez-spinner />
      </div>
      <ez-load-more
        v-if="meta.nextId && meta.nextValue && !isLoadingMore"
        @loadMore="fetchMoreSuppliers"
      />
      <ez-loader :show="isLoading">Loading...</ez-loader>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.sync-table tbody tr td {
  overflow: visible;
}
.supplier-name {
  display: flex;
  align-items: center;
}
.supplier-cell {
  width: 500px;
}
.select-all-checkbox {
  .input-group {
    @include font-size(12px);
    :deep() .check-indicator {
      margin-right: 8px;
    }
  }
}
.header {
  width: 400px;
  &__info-text {
    @include font-size(14px, 20px);
    font-weight: 500;
    color: $color-gray-6C;
  }
}

:deep() .ez-filter-list__items .search {
  width: 260px;
}

:deep() .select-search__trigger {
  min-width: 220px;
}
</style>
